import { CompanyContainer } from "components/company/v3/CompanyContainers";
import CompanyLocalState, { CompanyEventsLocalState } from "components/company/v3/CompanyLocalState";
import BrazilianAboutBox from "components/company/v3/brazilian/boxes/BrazilianAboutBox";
import BrazilianDiscussionsBox from "components/company/v3/brazilian/boxes/BrazilianDiscussionsBox";
import BrazilianEventsBox from "components/company/v3/brazilian/boxes/BrazilianEventsBox";
import BrazilianFinancialsBox from "components/company/v3/brazilian/boxes/BrazilianFinancialsBox";
import BrazilianGovernanceBox from "components/company/v3/brazilian/boxes/BrazilianGovernanceBox";
import BrazilianHighlightsBox from "components/company/v3/brazilian/boxes/BrazilianHighlightsBox";
import BrazilianLiquityBox from "components/company/v3/brazilian/boxes/BrazilianLiquityBox";
import OperatingSegmentsBox from "components/company/v3/boxes/OperatingSegmentsBox";
import ValuationBox from "components/company/v3/boxes/ValuationBox";
import BrazilianQuotesBox from "components/company/v3/brazilian/boxes/BrazilianQuotesBox";
import BrazilianCardsContent from "components/company/v3/brazilian/contents/about/BrazilianCardsContent";
import {
  CompanyAboutTabEnum,
  CompanyDiscussionsTabEnum,
  CompanyFinancialsTabEnum,
  CompanyGovernanceTabEnum,
  CompanyHighlightsTabEnum,
  CompanyLiquidityTabEnum,
  CompanyMenuSlugEnum,
  CompanyOperatingSegmentsTabEnum,
  CompanyQuotesTabEnum,
} from "components/company/v3/menu/enums";
import type { CompanyMenuSlug } from "components/company/v3/menu/types";
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import type { Selfie } from "lib/hooks/useSelfie";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { ISecurityCountry } from "types/graphqlTypes";

export default function useBrazilianBoxes(tickerSymbol: string | null) {
  const viewer = useSelfie();
  const { value: enabledIssuerPageBoxes } = useSpecificSetting("enabledIssuerPageBoxes.acoes");

  if (!tickerSymbol) {
    return {
      orderedMenu: [],
    };
  }

  if (
    !enabledIssuerPageBoxes ||
    (enabledIssuerPageBoxes && Array.isArray(enabledIssuerPageBoxes) && enabledIssuerPageBoxes.length === 0)
  ) {
    const systemDefaultOrder = [
      CompanyMenuSlugEnum.ABOUT,
      CompanyMenuSlugEnum.CARDS,
      CompanyMenuSlugEnum.QUOTES,
      CompanyMenuSlugEnum.EVENTS,
      CompanyMenuSlugEnum.FINANCIALS,
      CompanyMenuSlugEnum.VALUATION,
      CompanyMenuSlugEnum.DISCUSSIONS,
      CompanyMenuSlugEnum.HIGHLIGHTS,
    ];

    return {
      orderedMenu: orderMenuBoxesByPreferences(
        viewer,
        tickerSymbol.toUpperCase(),
        systemDefaultOrder as CompanyMenuSlug[],
      ),
    };
  }

  return {
    orderedMenu: orderMenuBoxesByPreferences(
      viewer,
      tickerSymbol.toUpperCase(),
      enabledIssuerPageBoxes as CompanyMenuSlug[],
    ),
  };
}

function orderMenuBoxesByPreferences(viewer: Selfie, tickerSymbol: string, preferredOrder: CompanyMenuSlug[]) {
  let preferredOrderForViewer = preferredOrder;

  if (viewer) {
    preferredOrderForViewer = preferredOrder.filter((slug) => slug !== CompanyMenuSlugEnum.VALUATION);
  } else if (!preferredOrder.includes(CompanyMenuSlugEnum.VALUATION)) {
    preferredOrderForViewer = insertAfter(
      preferredOrder,
      CompanyMenuSlugEnum.FINANCIALS,
      CompanyMenuSlugEnum.VALUATION,
    );
  }

  if (viewer?.isPremium) {
    const boxes = [
      {
        slug: CompanyMenuSlugEnum.ABOUT,
        box: (
          <CompanyLocalState<CompanyAboutTabEnum> defaultTab={CompanyAboutTabEnum.ABOUT}>
            {(props) => <BrazilianAboutBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.CARDS,
        box: (
          <CompanyContainer className="xl:h-full">
            <BrazilianCardsContent tickerSymbol={tickerSymbol} />
          </CompanyContainer>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.GOVERNANCE,
        box: (
          <CompanyLocalState<CompanyGovernanceTabEnum> defaultTab={CompanyGovernanceTabEnum.EQUITY_POSITION}>
            {(props) => <BrazilianGovernanceBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.LIQUIDITY,
        box: (
          <CompanyLocalState<CompanyLiquidityTabEnum> defaultTab={CompanyLiquidityTabEnum.TRADE_HISTORY}>
            {(props) => <BrazilianLiquityBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.OPERATING_SEGMENTS,
        box: (
          <CompanyLocalState<CompanyOperatingSegmentsTabEnum> defaultTab={CompanyOperatingSegmentsTabEnum.BUSINESS}>
            {(props) => <OperatingSegmentsBox tickerSymbol={tickerSymbol} country={ISecurityCountry.Br} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.FINANCIALS,
        box: (
          <CompanyLocalState<CompanyFinancialsTabEnum> defaultTab={CompanyFinancialsTabEnum.FINANCIALS}>
            {(props) => <BrazilianFinancialsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.VALUATION,
        box: <ValuationBox tickerSymbol={tickerSymbol} securityCountry={ISecurityCountry.Br} />,
      },
      {
        slug: CompanyMenuSlugEnum.QUOTES,
        box: (
          <CompanyLocalState<CompanyQuotesTabEnum> defaultTab={CompanyQuotesTabEnum.QUOTE}>
            {(props) => <BrazilianQuotesBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.EVENTS,
        box: (
          <CompanyEventsLocalState>
            {(props) => <BrazilianEventsBox tickerSymbol={tickerSymbol} {...props} isRenderPageDefault={true} />}
          </CompanyEventsLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.HIGHLIGHTS,
        box: (
          <CompanyLocalState<CompanyHighlightsTabEnum> defaultTab={CompanyHighlightsTabEnum.HIGHLIGHTS}>
            {(props) => <BrazilianHighlightsBox {...props} />}
          </CompanyLocalState>
        ),
      },
      {
        slug: CompanyMenuSlugEnum.DISCUSSIONS,
        box: (
          <CompanyLocalState<CompanyDiscussionsTabEnum> defaultTab={CompanyDiscussionsTabEnum.COMMENTS}>
            {(props) => <BrazilianDiscussionsBox tickerSymbol={tickerSymbol} {...props} />}
          </CompanyLocalState>
        ),
      },
    ];

    return preferredOrderForViewer.map((slug) => boxes.find((box) => box.slug === slug)).filter(notUndefinedOrNull);
  }
  const boxes = [
    {
      slug: CompanyMenuSlugEnum.ABOUT,
      box: (
        <CompanyLocalState<CompanyAboutTabEnum> defaultTab={CompanyAboutTabEnum.ABOUT}>
          {(props) => <BrazilianAboutBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.CARDS,
      box: (
        <CompanyContainer className="xl:h-full">
          <BrazilianCardsContent tickerSymbol={tickerSymbol} />
        </CompanyContainer>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.FINANCIALS,
      box: (
        <CompanyLocalState<CompanyFinancialsTabEnum> defaultTab={CompanyFinancialsTabEnum.FINANCIALS}>
          {(props) => <BrazilianFinancialsBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.VALUATION,
      box: <ValuationBox tickerSymbol={tickerSymbol} securityCountry={ISecurityCountry.Br} />,
    },
    {
      slug: CompanyMenuSlugEnum.QUOTES,
      box: (
        <CompanyLocalState<CompanyQuotesTabEnum> defaultTab={CompanyQuotesTabEnum.QUOTE}>
          {(props) => <BrazilianQuotesBox tickerSymbol={tickerSymbol} {...props} />}
        </CompanyLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.EVENTS,
      box: (
        <CompanyEventsLocalState>
          {(props) => <BrazilianEventsBox tickerSymbol={tickerSymbol} {...props} isRenderPageDefault={true} />}
        </CompanyEventsLocalState>
      ),
    },
    {
      slug: CompanyMenuSlugEnum.HIGHLIGHTS,
      box: (
        <CompanyLocalState<CompanyHighlightsTabEnum> defaultTab={CompanyHighlightsTabEnum.VIDEOS}>
          {(props) => <BrazilianHighlightsBox {...props} />}
        </CompanyLocalState>
      ),
    },
  ];

  return preferredOrderForViewer.map((slug) => boxes.find((box) => box.slug === slug)).filter(notUndefinedOrNull);
}

function insertAfter<T>(values: T[], existingValue: T, newValue: T) {
  const existingValueIndex = values.indexOf(existingValue);

  if (existingValueIndex === -1) {
    return [...values, newValue];
  }

  return [...values.slice(0, existingValueIndex + 1), newValue, ...values.slice(existingValueIndex + 1)];
}
